import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import extensionBridge from '../services/extensionBridge';
import ExtensionUpdate from './ExtensionUpdate';
import { Card, Chip, PillButton } from '../ui';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as RefreshIcon } from '../assets/icons/refresh-cw.svg';
import { ReactComponent as XIcon } from '../assets/icons/x.svg';
import './BrowserSettingsCards.css';

// Chrome, Edge, Brave, Arc y Opera comparten el paquete MV3. Firefox tiene su variante desde la fase 8; Safari
// (contenedor Xcode, pendiente de un Mac) llega despues. Nombres propios: no se traducen.
const BROWSERS = [
  { name: 'Chrome', supported: true },
  { name: 'Edge', supported: true },
  { name: 'Brave', supported: true },
  { name: 'Arc', supported: true },
  { name: 'Opera', supported: true },
  { name: 'Firefox', supported: true }, // fase 8: paquete propio (scripts/package-extension.mjs --firefox)
  { name: 'Safari', supported: false },
];

function StatusRow({ label, children }) {
  return (
    <div className="sgc-settings__row sgc-settings__row--inline">
      <span className="sgc-settings__row-title">{label}</span>
      <span className="sgc-settings__status-value">{children}</span>
    </div>
  );
}

/**
 * Pestaña Navegador: estado de la extension en ESTE navegador (la detecta el content script de la
 * extension en la propia pagina del panel), version, ultima sincronizacion y el boton de descarga.
 * `connectionEnabled` es el ajuste de la cuenta (browser.connectExtension).
 */
export function ExtensionStatusCard({ connectionEnabled }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(undefined); // undefined = comprobando, null = no detectada
  const [showModal, setShowModal] = useState(false);

  const check = useCallback(() => {
    setStatus(undefined);
    let cancelled = false;
    extensionBridge.getExtensionStatus().then((s) => { if (!cancelled) setStatus(s); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => check(), [check]);

  const lastSync = status?.lastSyncAt
    ? new Date(status.lastSyncAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : t('settings.browser.status.never');

  return (
    <Card className="sgc-settings__card" data-testid="extension-status">
      <h2 className="sgc-settings__card-title">{t('settings.browser.status.title')}</h2>
      <div className="sgc-settings__rows">
        <StatusRow label={t('settings.browser.status.extension')}>
          {status === undefined
            ? t('extension.checking')
            : <Chip label={status ? t('settings.browser.status.detected') : t('settings.browser.status.notDetected')} selected={!!status} />}
        </StatusRow>
        {status && (
          <>
            <StatusRow label={t('settings.browser.status.version')}>{status.version}</StatusRow>
            <StatusRow label={t('settings.browser.status.lastSync')}>{lastSync}</StatusRow>
          </>
        )}
        <StatusRow label={t('settings.browser.status.connection')}>
          {connectionEnabled === false ? t('settings.browser.status.connectionOff') : t('settings.browser.status.connectionOn')}
        </StatusRow>
        <div className="sgc-settings__actions">
          <PillButton icon={DownloadIcon} size="sm" onClick={() => setShowModal(true)}>
            {t('settings.browser.status.get')}
          </PillButton>
          <PillButton variant="secondary" icon={RefreshIcon} size="sm" onClick={check}>
            {t('settings.browser.status.refresh')}
          </PillButton>
        </div>
      </div>
      {showModal && <ExtensionUpdate onClose={() => { setShowModal(false); check(); }} />}
    </Card>
  );
}

export function SupportedBrowsersCard() {
  const { t } = useTranslation();
  return (
    <Card className="sgc-settings__card" data-testid="supported-browsers">
      <h2 className="sgc-settings__card-title">{t('settings.browser.supported.title')}</h2>
      <p className="sgc-settings__card-desc">{t('settings.browser.supported.desc')}</p>
      <ul className="sgc-settings__browsers">
        {BROWSERS.map((b) => (
          <li key={b.name} className="sgc-settings__browser">
            <span className="sgc-settings__row-title">{b.name}</span>
            <Chip label={b.supported ? t('settings.browser.supported.yes') : t('settings.browser.supported.later')} selected={b.supported} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Pestaña Autocompletado: "Nunca para este sitio". La lista vive en chrome.storage.sync de la
 * extension (autofill-offers.js la gestiona); aqui se lee y se edita por el puente. Sin extension en
 * este navegador no hay nada que leer: se dice dónde está.
 */
export function NeverSaveSitesCard() {
  const { t } = useTranslation();
  const [sites, setSites] = useState(undefined); // undefined = cargando, null = sin extension

  useEffect(() => {
    let cancelled = false;
    extensionBridge.getNeverSaveSites().then((list) => { if (!cancelled) setSites(list); });
    return () => { cancelled = true; };
  }, []);

  const remove = (site) => extensionBridge.removeNeverSaveSite(site).then((list) => { if (list) setSites(list); });
  const clear = () => extensionBridge.clearNeverSaveSites().then((list) => { if (list) setSites(list); });

  return (
    <Card className="sgc-settings__card" data-testid="never-save-sites">
      <h2 className="sgc-settings__card-title">{t('settings.autofill.neverSave.title')}</h2>
      <p className="sgc-settings__card-desc">{t('settings.autofill.neverSave.desc')}</p>
      {sites === undefined && <p className="sgc-settings__loading" role="status">{t('common.loading')}</p>}
      {sites === null && <p className="sgc-settings__row-desc">{t('settings.autofill.neverSave.noExtension')}</p>}
      {Array.isArray(sites) && sites.length === 0 && <p className="sgc-settings__row-desc">{t('settings.autofill.neverSave.empty')}</p>}
      {Array.isArray(sites) && sites.length > 0 && (
        <>
          <ul className="sgc-settings__sites">
            {sites.map((site) => (
              <li key={site} className="sgc-settings__site">
                <span className="sgc-settings__site-name">{site}</span>
                <button
                  type="button"
                  className="sgc-settings__site-remove"
                  aria-label={t('settings.autofill.neverSave.remove', { site })}
                  onClick={() => remove(site)}
                >
                  <XIcon width={16} height={16} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {sites.length > 1 && (
            <div className="sgc-settings__actions">
              <PillButton variant="secondary" size="sm" onClick={clear}>{t('settings.autofill.neverSave.clear')}</PillButton>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
